import { RegionShared } from "@1games-ru/region-shared";
import { GameSettingType } from "@1games-ru/region-shared/dist/enums/account/settings";
import { useCallback } from "react";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import RpcMpProvider from "@/core/rpc-mp-provider/rpc-mp-provider";
import { setAppliedSettings as setAppliedBasicSettings } from "@/domains/settings/basic/slice";
import { setAppliedSettings as setAppliedGameplaySettings } from "@/domains/settings/gameplay/slice";
import { setAppliedSettings as setAppliedSoundSettings } from "@/domains/settings/sound/slice";

export default function useGameSettings() {
  const dispatch = useAppDispatch();
  const getGameSettings = useCallback(async () => {
    const response = await RpcMpProvider.callClient<
      typeof RegionShared.Client.Requests.GetGameSettingsRequest,
      typeof RegionShared.Client.Responses.GetGameSettingsResponse
    >(RegionShared.Client.Events.Account.GetGameSettings, {});

    if (response instanceof RpcMpProviderSuccessResponse) {
      const basicSettings: Partial<Record<GameSettingType, unknown>> = {};
      const soundSettings: Partial<Record<GameSettingType, unknown>> = {};
      const gameplaySettings: Partial<Record<GameSettingType, unknown>> = {};

      Object.entries(response.data).forEach((item) => {
        const key = Number(item[0]) as unknown as GameSettingType;
        const value = item[1];

        switch (key) {
          case GameSettingType.SettingHUDInfoAmount:
          case GameSettingType.SettingSpeedometerStyle:
          case GameSettingType.SettingShowHotkeys:
          case GameSettingType.SettingShowChat:
          case GameSettingType.SettingShowMap:
          case GameSettingType.SettingShowHUD:
          case GameSettingType.SettingMainMenuTransparency:
            basicSettings[key] = value;
            break;

          case GameSettingType.SettingNotificationFadeSpeed:
            // Обработка скорости затухания уведомлений
            break;

          case GameSettingType.SettingGPSAssistant:
            // Обработка GPS ассистента
            break;

          case GameSettingType.SettingPhoneWallpaper:
            // Обработка обоев телефона
            break;
          case GameSettingType.SettingPhoneRingtone:
            // Обработка рингтона телефона
            break;
          case GameSettingType.SettingPhoneScale:
            // Обработка масштаба телефона
            break;
          case GameSettingType.SettingPhoneMarginLeft:
            // Обработка левого отступа телефона
            break;
          case GameSettingType.SettingPhoneMarginRight:
            // Обработка правого отступа телефона
            break;
          case GameSettingType.SettingPhoneSilentMode:
            // Обработка беззвучного режима телефона
            break;

          case GameSettingType.SettingMenuInterfaceMusicVolume:
          case GameSettingType.SettingInGameMusicVolume:
          case GameSettingType.SettingRadioVolume:
          case GameSettingType.SettingEnvironmentVolume:
          case GameSettingType.SettingVoiceChatAndPhoneVolume:
          case GameSettingType.SettingInterfaceSoundsVolume:
          case GameSettingType.SettingPhoneRingtoneVolume:
          case GameSettingType.SettingOverallVolume:
          case GameSettingType.SettingMenuMusic:
          case GameSettingType.SettingInterfaceSound:
          case GameSettingType.SettingMusicToggle:
          case GameSettingType.SettingNPCDialoguesVolume:
          case GameSettingType.SettingCaseOpeningVolume:
            soundSettings[key] = value;
            break;
          case GameSettingType.SettingWalkStyleType:
          case GameSettingType.SettingCruiseControlType:
          case GameSettingType.SettingFacialEmotionsType:
          case GameSettingType.SettingVehicleEnterType:
            gameplaySettings[key] = value;
            break;
          default:
            // Обработка неизвестного типа настроек
            break;
        }
      });

      dispatch(setAppliedBasicSettings(basicSettings));
      dispatch(setAppliedSoundSettings(soundSettings));
      dispatch(setAppliedGameplaySettings(gameplaySettings));
    }

    console.log({ response });
  }, []);

  const updateGameSettings = useCallback(
    async (data: Partial<Record<GameSettingType, unknown>>) => {
      const response = await RpcMpProvider.callClient<
        typeof RegionShared.Client.Requests.UpdateGameSettingsRequest,
        typeof RegionShared.Client.Responses.UpdateGameSettingsResponse
      >(RegionShared.Client.Events.Account.UpdateGameSettings, {
        settings: data,
      } as typeof RegionShared.Client.Requests.UpdateGameSettingsRequest);

      return response;
    },
    [],
  );

  const resetGameSettings = useCallback(
    async (
      settings: (typeof RegionShared.Client.Requests.ResetGameSettingsRequest)["settings"],
    ) => {
      const response = await RpcMpProvider.callClient<
        typeof RegionShared.Client.Requests.ResetGameSettingsRequest,
        typeof RegionShared.Client.Responses.ResetGameSettingsResponse
      >(RegionShared.Client.Events.Account.ResetGameSettings, {
        settings,
      });

      return response;
    },
    [],
  );

  return {
    getGameSettings,
    updateGameSettings,
    resetGameSettings,
  };
}
